/* eslint-disable max-lines-per-function */
import { useCallback, useState } from 'react';
import { useDeepCompareEffect, useUpdateEffect } from 'react-use';
import { isEmpty } from 'lodash';

import { EmergencyNotificationDismissedEvent } from '@dreryk/egabinet-emergency-notification-frontend-api';

import {
   AlertCircleIcon,
   ButtonVariant,
   Heading,
   IconFillColor,
   IconSize,
   IconWrapper,
   ModalConfirmButton,
   ModalWidth,
   StyledFlex,
   Text,
} from '@dreryk/react-components';
import { Translate } from '@dreryk/react-common';
import { useCqrsEvent } from '@dreryk/command-query-react-common';

import { hasOneElement, itemsCountVisitor } from '~libs.typescript';

import { translationKeys } from '../translations';
import { StyledEmergencyModalConfirmButton } from '../shared/StyledEmergencyModalConfirmButton';

import { StyledEmergencyNotificationAlertModal } from './EmergencyNotificationAlertModal.styled';
import { EmergencyNotificationAlertItem } from './EmergencyNotificationAlertItem';
import { EmergencyNotificationAcknowledgeActionInterceptor } from './interceptors/EmergencyNotificationAcknowledgeActionInterceptor';
import { StyledEmergencyNotificationAlertItemButton } from './EmergencyNotificationAlertItem.styled';
import { EmergencyNotificationAlert } from './types';
import { getEmergencyNotificationAlertModalConfig } from './EmergencyNotificationAlertModal.utils';

type EmergencyNotificationAlertModalProps = {
   readonly onClose: VoidFunction;
   readonly emergencyNotifications: EmergencyNotificationAlert[];
};

export const EmergencyNotificationAlertModal = (props: EmergencyNotificationAlertModalProps): JSX.Element => {
   const [emergencyNotifications, setEmergencyNotifications] = useState<EmergencyNotificationAlert[]>(
      props.emergencyNotifications
   );

   useCqrsEvent<EmergencyNotificationDismissedEvent>(
      'egabinet-emergency-notification-frontend-api:EmergencyNotificationDismissedEvent',
      (event) => {
         setEmergencyNotifications((previousEmergencyNotifications) => [
            ...previousEmergencyNotifications.map((notification) => ({
               ...notification,
               dismissed: notification.id === event.notificationId ? true : notification.dismissed,
            })),
         ]);
      }
   );

   useUpdateEffect(() => {
      if (isEmpty(emergencyNotifications)) {
         props.onClose();
      }
   }, [props.onClose, emergencyNotifications]);

   useDeepCompareEffect(() => {
      setEmergencyNotifications((previousEmergencyNotifications) => {
         return [
            ...previousEmergencyNotifications,
            ...props.emergencyNotifications.filter(
               (notification) =>
                  !previousEmergencyNotifications.some(
                     (previousNotification) => previousNotification.id === notification.id
                  )
            ),
         ];
      });
   }, [props.emergencyNotifications]);

   const removeEmergencyNotification = useCallback((notificationId: number) => {
      setEmergencyNotifications((previousEmergencyNotifications) => {
         return [...previousEmergencyNotifications.filter((notification) => notification.id !== notificationId)];
      });
   }, []);

   const acknowledgeAll = useCallback(() => {
      setEmergencyNotifications([]);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [props.onClose]);

   const { title, variant, description } = getEmergencyNotificationAlertModalConfig(emergencyNotifications);

   return (
      <StyledEmergencyNotificationAlertModal
         testId={'emergencyNotificationAlert'}
         $variant={variant}
         title={
            <StyledFlex direction={'column'} alignItems={'center'} width={'100%'} gap={'18px'}>
               <IconWrapper testId={'alert'} size={IconSize.LARGER} fillColor={IconFillColor.INHERIT}>
                  <AlertCircleIcon />
               </IconWrapper>
               <Heading testId={'alert'} type={'h1'} bold content={title} margin={'0px'} />
            </StyledFlex>
         }
         width={ModalWidth.WIDE}
      >
         <StyledFlex direction={'column'} gap={'18px'}>
            <Text testId={'description'}>
               <Translate>{description}</Translate>
            </Text>
            <StyledFlex direction={'column'} gap={'8px'}>
               {emergencyNotifications.map((item) => (
                  <EmergencyNotificationAlertItem
                     key={item.id}
                     emergencyNotification={item}
                     button={
                        hasOneElement(emergencyNotifications) || item.dismissed ? null : (
                           <EmergencyNotificationAcknowledgeActionInterceptor notificationId={item.id}>
                              <StyledEmergencyNotificationAlertItemButton
                                 testId={'acknowledge'}
                                 label={translationKeys.acknowledge}
                                 onClick={() => removeEmergencyNotification(item.id)}
                                 variant={ButtonVariant.PRIMARY}
                              />
                           </EmergencyNotificationAcknowledgeActionInterceptor>
                        )
                     }
                  />
               ))}
            </StyledFlex>
         </StyledFlex>

         {variant === 'informative' ? (
            <ModalConfirmButton
               testId={'close'}
               label={translationKeys.close}
               onClick={props.onClose}
               variant={ButtonVariant.PRIMARY}
            />
         ) : (
            itemsCountVisitor(emergencyNotifications, {
               visitEmpty: () => null,
               visitSingle: (emergencyNotification) => (
                  <EmergencyNotificationAcknowledgeActionInterceptor notificationId={emergencyNotification.id}>
                     <StyledEmergencyModalConfirmButton
                        testId={'acknowledge'}
                        label={translationKeys.acknowledge}
                        onClick={() => removeEmergencyNotification(emergencyNotification.id)}
                        variant={ButtonVariant.PRIMARY}
                     />
                  </EmergencyNotificationAcknowledgeActionInterceptor>
               ),
               visitMultiple: () => (
                  <EmergencyNotificationAcknowledgeActionInterceptor>
                     <StyledEmergencyModalConfirmButton
                        testId={'acknowledgeAll'}
                        variant={ButtonVariant.PRIMARY}
                        onClick={acknowledgeAll}
                        label={translationKeys.acknowledgeAll}
                     />
                  </EmergencyNotificationAcknowledgeActionInterceptor>
               ),
            })
         )}
      </StyledEmergencyNotificationAlertModal>
   );
};
